import { useState, useEffect, useCallback } from 'react'
import { learningService } from '../services/learningService'
import { UserLearningProgress, LearningAchievement } from '../types/learning'

export const useLearningProgress = (userId?: string) => {
  const [progress, setProgress] = useState<UserLearningProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [achievements, setAchievements] = useState<LearningAchievement[]>([])

  const fetchProgress = useCallback(async () => {
    if (!userId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const [userProgress, userAchievements] = await Promise.all([
        learningService.getUserProgress(userId),
        learningService.getUserAchievements(userId)
      ])

      setProgress(userProgress)
      setAchievements(userAchievements)
    } catch (err) {
      console.error('Error fetching learning progress:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch progress')
    } finally {
      setLoading(false)
    }
  }, [userId])

  const refreshProgress = useCallback(() => {
    fetchProgress()
  }, [fetchProgress])

  const checkNewAchievements = useCallback(async (): Promise<LearningAchievement[]> => {
    if (!userId) return []

    try {
      const newAchievements = await learningService.checkAndAwardAchievements(userId)
      if (newAchievements.length > 0) {
        // Update achievements list
        setAchievements(prev => [...newAchievements, ...prev])
        // Refresh full progress to get updated stats
        await fetchProgress()
      }
      return newAchievements
    } catch (err) {
      console.error('Error checking achievements:', err)
      return []
    }
  }, [userId, fetchProgress])

  useEffect(() => {
    fetchProgress()
  }, [fetchProgress])

  return {
    progress,
    achievements,
    loading,
    error,
    refreshProgress,
    checkNewAchievements
  }
}

export const useLearningStats = () => {
  const [stats, setStats] = useState<{
    totalVideosWatched: number
    totalXPEarned: number
    currentStreak: number
    modulesCompleted: number
    level: number
    xpToNextLevel: number
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = useCallback(async (userId: string) => {
    try {
      setLoading(true)
      setError(null)
      
      const userProgress = await learningService.getUserProgress(userId)
      
      setStats({
        totalVideosWatched: userProgress.total_videos_watched,
        totalXPEarned: userProgress.total_xp_earned,
        currentStreak: userProgress.current_streak,
        modulesCompleted: userProgress.modules_completed,
        level: userProgress.level,
        xpToNextLevel: userProgress.xp_to_next_level
      })
    } catch (err) {
      console.error('Error fetching learning stats:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch stats')
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    stats,
    loading,
    error,
    fetchStats
  }
}
